import { axiosBaseQuery } from '@client';
import { getEnvironmentVariables } from '@common';
import { createApi } from '@reduxjs/toolkit/query/react';
import { RequestCaseQuotationsEvaluationResponse } from '@state';
import { PublicRfQAuthRequest } from '@state/types/ShareCaseRequestRequestDTO';
import axios from 'axios';
import { createQuotationExpenseKey } from '../restApi/secured/requestCaseApi';

const { API_URL } = getEnvironmentVariables();

const axiosWithoutInterceptors = axios.create({
  baseURL: `${API_URL}/da-app`
});

export const publicRfQPageApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: '/shared',
    customAxiosInstance: axiosWithoutInterceptors
  }),
  reducerPath: 'public-rfq-page_api',
  endpoints: (build) => ({
    submitRequestForRfQAccess: build.query<
      RequestCaseQuotationsEvaluationResponse,
      PublicRfQAuthRequest
    >({
      query: ({ requestCaseId, ...payload }) => ({
        url: `/request-case/${requestCaseId}`,
        method: 'POST',
        data: payload
      }),
      transformResponse(res: RequestCaseQuotationsEvaluationResponse) {
        const portExpenses = (res.portExpenses ?? [])
          .slice()
          .map((e) => ({
            ...e,
            expenseGroupName: 'Port Expenses',
            key: createQuotationExpenseKey(e)
          }))
          .sort((a, b) => {
            return a.expenseOrder - b.expenseOrder;
          });

        const agencyExpenses = (res.agencyExpenses ?? [])
          .slice()
          .map((e) => ({
            ...e,
            expenseGroupName: 'Agency Expenses',
            key: createQuotationExpenseKey(e)
          }))
          .sort((a, b) => {
            return a.expenseOrder - b.expenseOrder;
          });

        const husbandryExpenses = (res.husbandryExpenses ?? [])
          .slice()
          .map((e) => ({ ...e, key: createQuotationExpenseKey(e) }))
          .sort((a, b) => {
            if (a.expenseGroupOrder !== b.expenseGroupOrder) {
              return a.expenseGroupOrder - b.expenseGroupOrder;
            }
            return a.expenseOrder - b.expenseOrder;
          });

        const quotations = (res.quotations ?? []).map((q) => ({
          ...q,
          husbandryExpensesSection: {
            ...q.husbandryExpensesSection,
            expenses: (q.husbandryExpensesSection.expenses ?? [])
              .slice()
              .sort((a, b) => {
                if (a.expenseGroupOrder !== b.expenseGroupOrder) {
                  return a.expenseGroupOrder - b.expenseGroupOrder;
                }
                return a.expenseOrder - b.expenseOrder;
              })
              .map((e) => ({
                ...e,
                key: createQuotationExpenseKey(e),
                currency: e.currency || res.currency
              }))
          },
          portExpensesSection: {
            ...q.portExpensesSection,
            expenses: (q.portExpensesSection.expenses ?? []).map((e) => ({
              ...e,
              key: createQuotationExpenseKey(e),
              currency: e.currency || res.currency
            }))
          },
          agencyExpensesSection: {
            ...q.agencyExpensesSection,
            expenses: (q.agencyExpensesSection.expenses ?? []).map((e) => ({
              ...e,
              key: createQuotationExpenseKey(e),
              currency: e.currency || res.currency
            }))
          }
        }));
        return {
          ...res,
          quotations,
          husbandryExpenses,
          portExpenses,
          agencyExpenses
        };
      }
    })
  })
});

export type publicRfQPageState = {
  [publicRfQPageApi.reducerPath]: ReturnType<typeof publicRfQPageApi.reducer>;
};

export const {
  useSubmitRequestForRfQAccessQuery,
  useLazySubmitRequestForRfQAccessQuery
} = publicRfQPageApi;
